/* New Design - MeetPageHeader
 * Shared header for every meet-scoped page (details, edit, registrations,
 * start lists, results, training, points). Enforces one consistent layout:
 *   - title  = the page's function (never the meet name)
 *   - subtitle = prominent meet name + muted date (+ muted start time when
 *     supplied, matching the club dashboard's "date · time" presentation)
 *   - a "Back to Meet" button + any extra actions, in a wrapping row
 */

import React from 'react';
import PropTypes from 'prop-types';
import PageHeader from 'components/NewDesign/PageHeader';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import { ReactComponent as ChevronLeftIcon } from 'assets/images/icon_chevron_left.svg';
import styles from './MeetPageHeader.module.css';

const MeetPageHeader = ({
  title,
  meetName,
  meetDate,
  meetTime,
  subTitle,
  titleBadge,
  onBackToMeet,
  children,
}) => {
  // An explicit subTitle overrides the meet name/date composition (e.g. the
  // "Add Meet" screen, which has no meet yet). It renders in the settings-page
  // style (title color, regular weight) since it's a descriptive sentence, not
  // a meet name.
  const hasExplicitSubTitle = subTitle !== undefined;
  // "02 Dec 2024 · 6:00 AM" — the time only ever renders alongside a date.
  const dateTimeLabel = meetDate
    ? meetTime
      ? `${meetDate} · ${meetTime}`
      : meetDate
    : '';
  let resolvedSubTitle;
  if (hasExplicitSubTitle) {
    resolvedSubTitle = subTitle;
  } else if (meetName) {
    resolvedSubTitle = (
      <>
        <span className={styles.meetName}>{meetName}</span>
        {dateTimeLabel ? (
          <span className={styles.meetDate}> · {dateTimeLabel}</span>
        ) : null}
      </>
    );
  } else {
    resolvedSubTitle = dateTimeLabel;
  }

  return (
    <PageHeader
      title={title}
      subTitle={resolvedSubTitle}
      titleBadge={titleBadge}
      flushSubTitle
      primarySubTitle={hasExplicitSubTitle}
      regularWeightSubTitle={hasExplicitSubTitle}
    >
      <div className={styles.actions}>
        {onBackToMeet && (
          <CustomNewButton
            variant="outline"
            icon={<ChevronLeftIcon />}
            onClick={onBackToMeet}
          >
            Back to Meet
          </CustomNewButton>
        )}
        {children}
      </div>
    </PageHeader>
  );
};

MeetPageHeader.propTypes = {
  title: PropTypes.string,
  meetName: PropTypes.string,
  meetDate: PropTypes.string,
  meetTime: PropTypes.string,
  subTitle: PropTypes.node,
  titleBadge: PropTypes.node,
  onBackToMeet: PropTypes.func,
  children: PropTypes.node,
};

export default MeetPageHeader;
